import { ConditionalExcept, ConditionalKeys } from "type-fest";

// eslint-disable-next-line no-restricted-imports
import { DECRYPT_ERROR, EncString, SymmetricCryptoKey } from "@bitwarden/legacy-crypto";

import { View } from "../../../models/view/view";
import { Utils } from "../../misc/utils";

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
type EncStringKeys<T> = ConditionalKeys<ConditionalExcept<T, Function>, EncString>;
export type DecryptedObject<
  TEncryptedObject,
  TDecryptedKeys extends EncStringKeys<TEncryptedObject>,
> = Record<TDecryptedKeys, string> & Omit<TEncryptedObject, TDecryptedKeys>;

// extracts shared keys from the domain and view types
type EncryptableKeys<D extends Domain, V extends View> = (keyof D &
  ConditionalKeys<D, EncString | null | undefined>) &
  (keyof V & ConditionalKeys<V, string | null | undefined>);

type DomainEncryptableKeys<D extends Domain> = {
  [key in ConditionalKeys<D, EncString | null | undefined>]?: EncString | null | undefined;
};

type ViewEncryptableKeys<V extends View> = {
  [key in ConditionalKeys<V, string | null | undefined>]?: string | null | undefined;
};

// https://contributing.bitwarden.com/architecture/clients/data-model#domain
export default class Domain {
  protected buildDomainModel<D extends Domain>(
    domain: D,
    dataObj: any,
    map: any,
    notEncList: any[] = [],
  ) {
    for (const prop in map) {
      // eslint-disable-next-line
      if (!map.hasOwnProperty(prop)) {
        continue;
      }

      const objProp = dataObj[map[prop] || prop];
      if (notEncList.indexOf(prop) > -1) {
        (domain as any)[prop] = objProp ? objProp : null;
      } else {
        (domain as any)[prop] = objProp ? new EncString(objProp) : null;
      }
    }
  }

  protected buildDataModel<D extends Domain>(
    domain: D,
    dataObj: any,
    map: any,
    notEncStringList: any[] = [],
  ) {
    for (const prop in map) {
      // eslint-disable-next-line
      if (!map.hasOwnProperty(prop)) {
        continue;
      }

      const objProp = (domain as any)[map[prop] || prop];
      if (notEncStringList.indexOf(prop) > -1) {
        (dataObj as any)[prop] = objProp != null ? objProp : null;
      } else {
        (dataObj as any)[prop] = objProp != null ? (objProp as EncString).encryptedString : null;
      }
    }
  }

  /** @deprecated - Domain encryption must be implemented in the SDK */
  protected async decryptObj<D extends Domain, V extends View>(
    domain: DomainEncryptableKeys<D>,
    viewModel: ViewEncryptableKeys<V>,
    props: EncryptableKeys<D, V>[],
    key: SymmetricCryptoKey,
    objectContext: string = "No Domain Context",
  ): Promise<V> {
    const encryptService = Utils.getContainerService().getEncryptService();
    for (const prop of props) {
      if (domain[prop] == null) {
        viewModel[prop] = null;
        continue;
      }
      try {
        viewModel[prop] = await encryptService.decryptString(domain[prop]!, key);
      } catch (e) {
        // In case the SDK maps to a non-Error type, this is defensive
        const errorMsg =
          typeof e === "object" && e !== null && "message" in e
            ? (e as { message: string }).message
            : String(e);

        // eslint-disable-next-line no-console
        console.error(
          `Failed to decrypt property '${String(
            prop,
          )}' of domain. Context: ${objectContext}. Error: ${errorMsg}`,
        );
        viewModel[prop] = DECRYPT_ERROR;
      }
    }
    return viewModel as V;
  }
}
